import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import empty from "../../assets/images/empty-box.png";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuestionCircle,
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faFileDownload,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { baseUrl } from "../../config/baseUrl";

export default function ListParagraph() {
  const [paragraph, setParagraph] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [subtopicFilter, setSubtopicFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [paragraphsPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalParagraphs, setTotalParagraphs] = useState(0);
  const fileInputRef = useRef(null);
  let [excelSearch, setExcelSearch] = useState(
    new URLSearchParams({
      name: searchTerm,
      status: statusFilter,
    })
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchAllData();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [
    searchTerm,
    currentPage,
    statusFilter,
    subjectFilter,
    topicFilter,
    subtopicFilter,
    sectionFilter,
  ]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: paragraphsPerPage.toString(),
      });

      if (searchTerm) params.append("name", searchTerm);
      if (statusFilter) params.append("status", statusFilter);
      if (subjectFilter) params.append("subject", subjectFilter);
      if (topicFilter) params.append("topic", topicFilter);
      if (subtopicFilter) params.append("subtopic", subtopicFilter);
      if (sectionFilter) params.append("section", sectionFilter);
      setExcelSearch(
        new URLSearchParams({
          name: searchTerm,
          status: statusFilter,
          subject: subjectFilter,
          topic: topicFilter,
          subtopic: subtopicFilter,
          section: sectionFilter,
        })
      );

      const [
        paragraphsResponse,
        subjectsResponse,
        sectionResponse,
        topicsResponse,
        subtopicsResponse,
      ] = await Promise.all([
        fetch(`${baseUrl}/passage?${params.toString()}`, {
          headers: { Authorization: localStorage.getItem("token") },
        }),
        fetch(`${baseUrl}/subjects`, {
          headers: { Authorization: localStorage.getItem("token") },
        }),
        fetch(`${baseUrl}/sections`, {
          headers: { Authorization: localStorage.getItem("token") },
        }),
        fetch(`${baseUrl}/topics?subject=${subjectFilter}`, {
          headers: { Authorization: localStorage.getItem("token") },
        }),
        fetch(`${baseUrl}/subtopics?${params.toString()}`, {
          headers: { Authorization: localStorage.getItem("token") },
        }),
      ]);

      const paragraphsData = await paragraphsResponse.json();
      const subjectsData = await subjectsResponse.json();
      const sectionData = await sectionResponse.json();
      const topicsData = await topicsResponse.json();
      const subtopicsData = await subtopicsResponse.json();

      if (paragraphsData.status) {
        setParagraph(paragraphsData.data || []);
        setTotalPages(paragraphsData.pageInfo?.totalPages || 1);
        setTotalParagraphs(paragraphsData.pageInfo?.total || 0);
      }

      if (subjectsData.status) {
        setSubjects(subjectsData.data || []);
      }
      if (sectionData.status) {
        setSections(sectionData.data || []);
      }

      if (topicsData.status) {
        setTopics(topicsData.data || []);
      }

      if (subtopicsData.status) {
        setSubtopics(subtopicsData.data || []);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
      setParagraph([]);
      setSubjects([]);
      setTopics([]);
      setSubtopics([]);
      setLoading(false);
    }
  };

  const handleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    try {
      const response = await fetch(`${baseUrl}/passage/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();

      if (data.status) {
        // Update local state immediately for better UX
        setParagraph((prevParagraphs) =>
          prevParagraphs.map((p) =>
            p._id === id ? { ...p, status: newStatus } : p
          )
        );
        toast.success("Status updated successfully");
      } else {
        toast.error(data.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${baseUrl}/passage/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        });

        const data = await response.json();

        if (data.status) {
          // Update local state immediately
          setParagraph((prevParagraphs) =>
            prevParagraphs.filter((p) => p._id !== id)
          );
          setTotalParagraphs((prev) => prev - 1);
          toast.success("Paragraph deleted successfully");
        } else {
          toast.error(data.message || "Failed to delete paragraph");
        }
      } catch (error) {
        console.error("Error deleting paragraph:", error);
        toast.error("Failed to delete paragraph");
      }
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const apiCall = async (file) => {};

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, subjectFilter, topicFilter, subtopicFilter]);

  return (
    <>
      <section className="main-sec">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-6">
              <div className="dashboard-title">
                <h4 className="dash-head">
                  <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                  Paragraphs Management
                </h4>
              </div>
              <div className="custom-bredcump">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Paragraphs List
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-end align-items-start">
              <Link to="add" className="btn-add-paragraph">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Paragraph
              </Link>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="cards bus-list">
                <div className="bus-filter">
                  <div className="row align-items-center">
                    <div className="col-lg-6">
                      <h5 className="card-title">
                        Paragraph List ({paragraph.length} of {totalParagraphs})
                      </h5>
                    </div>
                    <div className="col-lg-6 text-end">
                      <small className="text-muted">
                        Page {currentPage} of {totalPages} | Total:{" "}
                        {totalParagraphs}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="table-responsive custom-table">
                  <table className="table table-borderless">
                    <thead>
                      <tr>
                        <th>SN</th>
                        <th>Title</th>
                        <th>Section</th>
                        <th>Subject</th>
                        <th>Topic</th>
                        <th>Subtopic</th>
                        <th>Language</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="loading-spinner">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mt-2">Loading paragraphs...</p>
                          </td>
                        </tr>
                      ) : paragraph.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="empty-state">
                            <img
                              src={empty || "/placeholder.svg"}
                              alt="No paragraphs"
                              width="200px"
                            />
                            <h4>No Paragraphs found!</h4>
                            <p className="text-muted">
                              {searchTerm ||
                              statusFilter ||
                              subjectFilter ||
                              topicFilter ||
                              subtopicFilter
                                ? "Try adjusting your search or filter criteria"
                                : "Start by adding your first paragraph"}
                            </p>
                            {!searchTerm &&
                              !statusFilter &&
                              !subjectFilter &&
                              !topicFilter &&
                              !subtopicFilter && (
                                <Link
                                  to="create"
                                  className="btn btn-primary mt-3"
                                >
                                  <FontAwesomeIcon
                                    icon={faPlus}
                                    className="me-2"
                                  />
                                  Add Your First Paragraph
                                </Link>
                              )}
                          </td>
                        </tr>
                      ) : (
                        paragraph.map((item, i) => (
                          <tr key={item?._id}>
                            <td>
                              {(currentPage - 1) * paragraphsPerPage + i + 1}
                            </td>
                            {/* <td>{question?.questionCode}</td> */}
                            <td>
                              <div className="question-text">
                                <div
                                  className="fw-medium"
                                  dangerouslySetInnerHTML={{
                                    __html: truncateText(item.title, 50),
                                  }}
                                />
                              </div>
                            </td>
                            <td>
                              <span
                                className="subject-badge"
                                style={{ "text-wrap": "nowrap" }}
                              >
                                {item.section_id?.name || "N/A"}
                              </span>
                            </td>
                            <td>
                              <span className="subject-badge">
                                {item.subject_id?.name || "N/A"}
                              </span>
                            </td>
                            <td>
                              <span className="text-muted">
                                {truncateText(
                                  item.topic_id?.topic_name || "N/A",
                                  30
                                )}
                              </span>
                            </td>
                            <td>
                              <span className="text-muted">
                                {truncateText(
                                  item.subtopic_id?.sub_topic_name || "N/A",
                                  30
                                )}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-info text-capitalize">
                                {item.language?.name || "N/A"}
                              </span>
                            </td>
                            <td>
                              <button
                                onClick={() =>
                                  handleStatus(item?._id, item?.status)
                                }
                                className={`status-badge ${
                                  item.status === "active"
                                    ? "status-active"
                                    : "status-inactive"
                                }`}
                                disabled={loading}
                              >
                                {item?.status === "active"
                                  ? "Active"
                                  : "Inactive"}
                              </button>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/paragraph/edit/${item?._id}`}
                                  className="action-btn edit-btn"
                                  title="Edit item"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Link>
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => handleDelete(item?._id)}
                                  title="Delete item"
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination-container">
                    <nav aria-label="Questions pagination">
                      <ul className="pagination justify-content-center">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          // Show first page, last page, current page, and pages around current page
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 &&
                              pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <li
                                key={pageNumber}
                                className={`page-item ${
                                  currentPage === pageNumber ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => paginate(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              </li>
                            );
                          } else if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return (
                              <li
                                key={pageNumber}
                                className="page-item disabled"
                              >
                                <span className="page-link">...</span>
                              </li>
                            );
                          }
                          return null;
                        })}
                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <style>
        {`
        .subject-badge {
                    background-color: #e3f2fd;
                    color: #1976d2;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 0.8rem;
                    font-weight: 500;
                }
        
.btn-add-paragraph {
  background: #008080;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  color: white;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-add-paragraph:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  color: white;
}

        `}
      </style>
    </>
  );
}
